import React, { useState, useMemo, useEffect } from 'react';
import { Table, Box, Header, Link, Textarea, ButtonDropdown,Container, SpaceBetween, FormField } from '@cloudscape-design/components';
import { CompletedStatusComponent } from '../pages/application/table-select-filter-components';
import { parseAiRecommendationPercentageScore } from '../common/utility';
import { submissionsApi } from '../api/Submission';

const Framework2Table = ({ data, submissionId }) => {
  const [expandedItems, setExpandedItems] = useState([]);
  const [overallText, setOverallText] = useState('');
  const [overallJudgment, setOverallJudgment] = useState('');

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const isWordLimitExceeded = (text) => getWordCount(text) > 250;

  useEffect(() => {
    if (submissionId) {
      submissionsApi.getSubmissionOverallEffectiveness(submissionId)
        .then(response => {
          if (response.overall_effectiveness_text) {
            setOverallText(response.overall_effectiveness_text);
          }
          if (response.overall_effectiveness_judgment) {
            setOverallJudgment(response.overall_effectiveness_judgment);
          }
        })
        .catch(err => console.error('Error loading overall effectiveness:', err));
    }
  }, [submissionId]);

  // Flatten data - memoized
  const flatData = useMemo(() => {
    const flattenData = (aspects) => {
      const items = [];
      
      aspects.forEach(aspect => {
        // Check framework type by framework
        const isFramework1 = process.env.REACT_APP_FRAMEWORK == 1;
        
        if (isFramework1) {
          // Framework 1: Skip aspect, show only standards and indicators (2-level)
          const sections = aspect.sections || [];
          sections.forEach(section => {
            items.push({
              id: `section-${section.id}`,
              type: 'section',
              title: section.title,
              level: 1,
              parentId: null,
              data: section
            });

            const submissions = section.indicator_submissions || [];
            submissions.forEach(submission => {
              items.push({
                id: `indicator-${submission.id}`,
                type: 'indicator',
                title: submission.indicator.name,
                index: submission.indicator.index,
                level: 2,
                parentId: `section-${section.id}`,
                data: submission
              });
            });
          });
        } else {
          // Framework 2: Show all 3 levels (aspect → section → indicator)
          items.push({
            id: `aspect-${aspect.id}`,
            type: 'aspect',
            title: aspect.title,
            level: 1,
            parentId: null,
            data: aspect
          });

          const sections = aspect.sections || [];
          sections.forEach(section => {
            items.push({
              id: `section-${section.id}`,
              type: 'section',
              title: section.title,
              level: 2,
              index: section.index,
              parentId: `aspect-${aspect.id}`,
              data: section
            });

            const submissions = section.indicator_submissions || [];
            submissions.forEach(submission => {
              items.push({
                id: `indicator-${submission.id}`,
                type: 'indicator',
                index: submission.indicator.index,
                title: submission.indicator.name,
                level: 3,
                parentId: `section-${section.id}`,
                data: submission
              });
            });
          });
        }
      });

      return items;
    };
    return flattenData(data);
  }, [data]);

  const visibleItems = useMemo(() => {
    const expandedSet = new Set(expandedItems.map(item => item.id));
    const visible = [];

    flatData.forEach(item => {
      if (item.level === 1) {
        visible.push(item);
      } else if (item.level === 2 && expandedSet.has(item.parentId)) {
        visible.push(item);
      } else if (item.level === 3 && expandedSet.has(item.parentId)) {
        visible.push(item);
      }
    });

    return visible;
  }, [flatData, expandedItems]);

  const handleExpandToggle = ({ detail }) => {
    setExpandedItems(prev => {
      const expandedSet = new Set(prev.map(item => item.id));
      
      if (detail.expanded) {
        expandedSet.add(detail.item.id);
      } else {
        // Remove the item and all its children
        expandedSet.delete(detail.item.id);
        
        // Find and remove all child items
        const removeChildren = (parentId) => {
          flatData.forEach(item => {
            if (item.parentId === parentId) {
              expandedSet.delete(item.id);
              removeChildren(item.id); // Recursively remove grandchildren
            }
          });
        };
        
        removeChildren(detail.item.id);
      }
      
      return Array.from(expandedSet).map(id => ({ id }));
    });
  };

  const columnDefinitions = [
    {
      id: 'title',
      header: 'Title',
      width: 500,
      cell: item => {
        const indent = (item.level - 1) * 20;
        const hasChildren = flatData.some(child => child.parentId === item.id);
        const isExpanded = expandedItems.some(exp => exp.id === item.id);
        
        return (
          <div style={{ 
            paddingLeft: `${indent}px`, 
            display: 'flex', 
            alignItems: item.type === 'indicator' ? 'flex-start' : 'center',
          }}>
            {hasChildren && (
              <button 
                onClick={() => handleExpandToggle({ detail: { expanded: !isExpanded, item } })}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  marginRight: '8px',
                  fontSize: '12px',
                  flexShrink: 0,
                  marginTop: item.type === 'indicator' ? '2px' : '0'
                }}
              >
                {isExpanded ? '\u25bc' : '\u25b6'}
              </button>
            )}
            {item.type === 'indicator' ? (
              <Link href={`/indicator-submission/${item.data.id}`} title={`${item.index} : ${item.title}`}>
                <div style={{
                  width: '800px',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  lineHeight: '1.4'
                }}>
                  {item.index} : {item.title}
                </div>
              </Link>
            ) : (
              <span style={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.4',
                maxWidth: '100%'
              }}>{item.index}  {item.title}</span>
            )}
          </div>
        );
      },
      isRowHeader: true
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => item.type === 'indicator' ? <CompletedStatusComponent status={item.data.is_complete} /> : null,
      width: 80
    },
    {
      id: 'ai_score',
      header: 'AI Score',
      cell: item => {
        if (item.type === 'indicator' && item.data.latest_score != null) {
          return `${parseAiRecommendationPercentageScore(item.data.latest_score)}%`;
        }
        return '';
      },
      width: 70
    }
  ];

  return (
    <div style={{ overflow: 'visible' }}>
      <SpaceBetween size="l">
        <Table
          trackBy="id"
          columnDefinitions={columnDefinitions}
          items={visibleItems}
          header={
            <Header
              counter={data.length ? `(${data.length})` : ''}
              description={process.env.REACT_APP_FRAMEWORK == 1 ? 'Standards and their indicators for this application' : 'Aspects and criteria and their indicators for this application'}
            >
              Annual Submission Details
            </Header>
          }
          empty={
            <Box textAlign="center">
              <b>No data available</b>
            </Box>
          }
        />
      
        <Container padding="m" style={{ border: '1px solid rgb(3, 20, 36)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Header variant="h2">Overall Effectiveness Self-Evaluation
            <span  style={{ marginLeft: '20px' }} >
            <ButtonDropdown
             
              items={[
                { id: "Outstanding", text: "Outstanding" },
                { id: "Good", text: "Good" },
                { id: "Satisfactory", text: "Satisfactory" },
                { id: "Inadequate", text: "Inadequate" },
              ]}
              onItemClick={({ detail }) => {
                const selected = detail.id;
                setOverallJudgment(selected);
                if (submissionId) {
                  submissionsApi.updateSubmissionOverallEffectivenessJudgment(submissionId, selected)
                    .catch(err => console.error('Error updating judgment:', err));
                }
              }}
            >
              {overallJudgment ? `Judgment: ${overallJudgment}` : "Set Judgment"}
            </ButtonDropdown>
            </span>
          </Header>
          </div>
        <SpaceBetween size="m">
          <FormField label="Overall Effectiveness Text (Maximum 250 words)">
            <Textarea
              value={overallText}
              onChange={({ detail }) => {
                setOverallText(detail.value);
                if (submissionId) {
                  submissionsApi.updateSubmissionOverallEffectivenessText(submissionId, detail.value)
                    .catch(err => console.error('Error updating text:', err));
                }
              }}
              placeholder="Enter your overall effectiveness self-evaluation..."
              rows={6}
              invalid={isWordLimitExceeded(overallText)}  />
            {isWordLimitExceeded(overallText) && (
              <div style={{ 
                fontSize: '12px', 
                color: '#d91515', 
                marginTop: '4px' 
              }}>
                Error: Text exceeds the 250-word limit. Please reduce the content.
              </div>
            )}
            <div style={{ 
              fontSize: '12px', 
              color: isWordLimitExceeded(overallText) ? '#d91515' : '#666', 
              marginTop: '4px' 
            }}>
              {`${getWordCount(overallText)}/250 words`}
            </div>
          </FormField>
          
          
        </SpaceBetween>
        </Container>
      </SpaceBetween>
    </div>
  );
};

export default Framework2Table;